
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Chart,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";


Chart.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const monthsFull = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const AnalyticsSuccessBarGraph = () => {
  const [bookedCounts, setBookedCounts] = useState(new Array(12).fill(0));
  const [cancelledCounts, setCancelledCounts] = useState(new Array(12).fill(0));
  const nonBookedCounts = [0];
  const [yearFilter, setYearFilter] = useState("last12");
  const [yearOptions, setYearOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [labels, setLabels] = useState(monthsFull);
  const [events, setEvents] = useState([]);
  const [cancelledEvents, setCancelledEvents] = useState([]);

  const isLast12 = yearFilter === "last12";

  const getFilterRange = () => {
    const now = new Date();
    if (isLast12) {
      const to = new Date();
      const from = new Date(to);
      from.setMonth(to.getMonth() - 11);
      from.setDate(1);
      return { from, to };
    } else {
      const from = new Date(`${yearFilter}-01-01`);
      const to = new Date(`${yearFilter}-12-31`);
      return { from, to };
    }
  };

  const generateLast12MonthLabels = () => {
    const now = new Date();
    const endMonth = now.getMonth();
    const labels = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(endMonth - i);
      labels.push(monthsFull[date.getMonth()]);
    }
    return labels;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const [bookedRes, cancelledRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/business/events`, { withCredentials: true }),
          axios.get(`${process.env.REACT_APP_API_URL}/business/cancelledEvents`, { withCredentials: true }),
        ]);

        const allEvents = bookedRes.data.data || [];
        const allCancelled = cancelledRes.data.data || [];

        setEvents(allEvents);
        setCancelledEvents(allCancelled);

        const yearsSet = new Set();
        allEvents.concat(allCancelled).forEach((event) => {
          const year = new Date(event.createdAt).getFullYear();
          yearsSet.add(year);
        });

        const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
        setYearOptions(["last12", ...sortedYears.map(String)]);
      } catch (err) {
        console.error("Failed to fetch events:", err);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const { from, to } = getFilterRange();
    const tempBooked = new Array(12).fill(0);
    const tempCancelled = new Array(12).fill(0);

    events.forEach((event) => {
      const eventDate = new Date(event.createdAt);
      if (eventDate >= from && eventDate <= to) {
        if (isLast12) {
          const diffInMonths =
            (eventDate.getFullYear() - from.getFullYear()) * 12 +
            eventDate.getMonth() -
            from.getMonth();
          if (diffInMonths >= 0 && diffInMonths < 12) {
            tempBooked[diffInMonths]++;
          }
        } else {
          tempBooked[eventDate.getMonth()]++;
        }
      }
    });

    cancelledEvents.forEach((event) => {
      const eventDate = new Date(event.createdAt);
      if (eventDate >= from && eventDate <= to) {
        if (isLast12) {
          const diffInMonths =
            (eventDate.getFullYear() - from.getFullYear()) * 12 +
            eventDate.getMonth() -
            from.getMonth();
          if (diffInMonths >= 0 && diffInMonths < 12) {
            tempCancelled[diffInMonths]++;
          }
        } else {
          tempCancelled[eventDate.getMonth()]++;
        }
      }
    });

    if (isLast12) {
      setLabels(generateLast12MonthLabels());
    } else {
      setLabels(monthsFull);
    }

    setBookedCounts(tempBooked);
    setCancelledCounts(tempCancelled);
  }, [yearFilter, events, cancelledEvents]);

  const data = {
    labels,
    datasets: [
      {
        label: "Booked",
        data: bookedCounts,
        backgroundColor: "#21A62C",
      },
      {
        label: "Non-booked",
        data: nonBookedCounts,
        backgroundColor: "#F99600",
      },
      {
        label: "Cancelled",
        data: cancelledCounts,
        backgroundColor: "#E02121",
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          usePointStyle: false,
          boxWidth: 12,
        },
      },
      title: {
        display: false,
      },
    },
    layout: {
      padding: { top: 20 },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div style={{ height: "100%", backgroundColor: "#FFFFFF", padding: "3%" }}>
      {/* Heading and Filter */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1.5rem",
        paddingRight: "1rem",
      }}>
        <h2 style={{
          color: "#1C1154",
          fontFamily: "Poppins",
          fontSize: "24px",
          fontWeight: "500",
          margin: 0,
        }}>
          Success Rate
        </h2>

        {/* Custom Dropdown */}
        <div style={{ position: "relative", width: "160px" }}>
          <div
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              backgroundColor: "#fff",
              padding: "10px 14px",
              paddingRight: "36px",
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "14px",
              color: "#625C73",
              position: "relative",
              userSelect: "none",
            }}
          >
            {yearFilter === "last12" ? "Last 12 Months" : `Year ${yearFilter}`}
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              style={{
                position: "absolute",
                right: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="#625C73"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {dropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "105%",
                left: 0,
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                zIndex: 10,
                padding: "6px 0",
              }}
            >
              {yearOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => {
                    setYearFilter(option);
                    setDropdownOpen(false);
                  }}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#4D4D4D",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#F3F3F3"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  {option === "last12" ? "Last 12 Months" : `Year ${option}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Bar data={data} options={options} />
    </div>
  );
};

export default AnalyticsSuccessBarGraph;
